import React, { useState, useEffect } from 'react';
import { ref, query, orderByChild, equalTo, get, set } from 'firebase/database';
import { database } from '../firebase/config';
import { useUserAuth } from '../contexts/UserAuthContext';
import { FormResponse } from '../types';
import {
  Container,
  Card,
  Button,
  Title,
  Subtitle
} from '../styles/GlobalStyles';
import styled from 'styled-components';
import FormWizard from '../components/FormWizard';
import ResponseDetails from './ResponseDetails';
import ThankYou from './ThankYou';

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

const WelcomeSection = styled.div`
  h1 {
    margin-bottom: 8px;
  }
`;

const ResponsesList = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 32px;
`;

const ResponseCard = styled(Card)`
  padding: 20px;
  border-left: 4px solid #0d6efd;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .response-info {
    flex: 1;
  }
  
  h3 {
    color: #212529;
    margin-bottom: 4px;
    font-size: 16px;
  }
  
  .date {
    color: #6c757d;
    font-size: 14px;
  }
`;

const ViewButton = styled(Button)`
  background: #28a745;
  padding: 8px 16px;
  font-size: 14px;
  
  &:hover {
    background: #218838;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6c757d;
  
  h3 {
    margin-bottom: 16px;
    color: #495057;
  }
  
  p {
    margin-bottom: 24px;
  }
`;

const LogoutButton = styled(Button)`
  background: #6c757d;
  
  &:hover {
    background: #5a6268;
  }
`;

const ProfileButton = styled.button`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  h2 {
    margin: 0;
    color: #212529;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #495057;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  color: #212529;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #0d6efd;
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
  }
  
  &:disabled {
    background-color: #e9ecef;
    color: #6c757d;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #0d6efd;
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
  }
`;

const DisabledNote = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: 4px 0 0 0;
  font-style: italic;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #dee2e6;
`;

const PartialResponseCard = styled(Card)`
  padding: 20px;
  border-left: 4px solid #ffc107;
  background: #fff8e1;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .partial-info {
    flex: 1;
  }
  
  h3 {
    color: #e65100;
    margin-bottom: 4px;
    font-size: 16px;
  }
  
  .date {
    color: #bf9000;
    font-size: 14px;
  }
`;

const ContinueButton = styled(Button)`
  background: #ffc107;
  color: #212529;
  padding: 8px 16px;
  font-size: 14px;
  
  &:hover {
    background: #e0a800;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Dashboard: React.FC = () => {
  const { currentUser, logout, refreshUserData, changePassword } = useUserAuth();
  const [responses, setResponses] = useState<FormResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState<FormResponse | null>(null);
  const [hasPartialResponse, setHasPartialResponse] = useState(false);
  const [partialResponseData, setPartialResponseData] = useState<any>(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    razaoSocial: '',
    nomeInstituicao: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    cep: '',
    whatsapp: '',
    site: '',
    instagram: '',
    nomeResponsavel: '',
    whatsappResponsavel: '',
    cargo: '',
    emailResponsavel: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    loadResponses();
    checkPartialResponse();
  }, [currentUser]);

  const checkPartialResponse = async () => {
    if (!currentUser) return;
    
    try {
      // Primeiro, verificar no Firebase
      const partialResponseRef = ref(database, `partialResponses/${currentUser.uid}`);
      const snapshot = await get(partialResponseRef);
      
      if (snapshot.exists()) {
        const firebasePartialResponse = snapshot.val();
        setHasPartialResponse(true);
        setPartialResponseData(firebasePartialResponse);
        
        // Sincronizar com localStorage para backup
        localStorage.setItem(`partial_response_${currentUser.uid}`, JSON.stringify(firebasePartialResponse));
        return;
      }
    } catch (error) {

    }
    
    // Se não encontrou no Firebase, verificar no localStorage como fallback
    const savedPartialResponse = localStorage.getItem(`partial_response_${currentUser.uid}`);
    if (savedPartialResponse) {
      try {
        const parsedResponse = JSON.parse(savedPartialResponse);
        setHasPartialResponse(true);
        setPartialResponseData(parsedResponse);
        
        // Sincronizar com Firebase
        try {
          const partialResponseRef = ref(database, `partialResponses/${currentUser.uid}`);
          await set(partialResponseRef, parsedResponse);
        } catch (error) {

        }
      } catch (error) {

        setHasPartialResponse(false);
      }
    } else {
      setHasPartialResponse(false);
    }
  };

  const loadResponses = async () => {
    if (!currentUser) return;
    
    try {
      const responsesRef = ref(database, 'responses');
      const responsesQuery = query(responsesRef, orderByChild('oscId'), equalTo(currentUser.uid));
      const snapshot = await get(responsesQuery);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        const responsesList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        })).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        
        setResponses(responsesList);
      } else {
        setResponses([]);
      }
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const handleFormComplete = () => {
    setShowForm(false);
    setShowThankYou(true);
    loadResponses();
    checkPartialResponse();
  };

  const handleReturnToDashboard = () => {
    setShowThankYou(false);
    loadResponses();
  };

  const handleOpenProfile = () => {
    if (currentUser?.oscData) {
      setProfileData({
        razaoSocial: currentUser.oscData.razaoSocial || '',
        nomeInstituicao: currentUser.oscData.nomeInstituicao || '',
        endereco: currentUser.oscData.endereco || '',
        numero: currentUser.oscData.numero || '',
        complemento: currentUser.oscData.complemento || '',
        bairro: currentUser.oscData.bairro || '',
        cidade: currentUser.oscData.cidade || '',
        uf: currentUser.oscData.uf || '',
        cep: currentUser.oscData.cep || '',
        whatsapp: currentUser.oscData.whatsapp || '',
        site: currentUser.oscData.site || '',
        instagram: currentUser.oscData.instagram || '',
        nomeResponsavel: currentUser.oscData.nomeResponsavel || '',
        whatsappResponsavel: currentUser.oscData.whatsappResponsavel || '',
        cargo: currentUser.oscData.cargo || '',
        emailResponsavel: currentUser.oscData.emailResponsavel || ''
      });
    }
    setShowProfileModal(true);
  };

  const handleCloseProfile = () => {
    setShowProfileModal(false);
  };

  const handleProfileInputChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    if (!currentUser?.uid) return;
    
    setProfileLoading(true);
    try {
      const oscRef = ref(database, `oscs/${currentUser.uid}`);
      const currentData = await get(oscRef);
      
      if (currentData.exists()) {
        const updatedData = {
          ...currentData.val(),
          ...profileData,
          updatedAt: new Date().toISOString()
        };
        
        await set(oscRef, updatedData);
        
        // Atualizar dados no contexto
        await refreshUserData();
        
        setShowProfileModal(false);
        alert('Dados atualizados com sucesso!');
      }
    } catch (error) {

      alert('Erro ao salvar dados. Tente novamente.');
    } finally {
       setProfileLoading(false);
     }
   };

   const handleFormCancel = () => {
     setShowForm(false);
     checkPartialResponse(); // Verificar se há resposta parcial após cancelar
  };

  const handleOpenPasswordModal = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handlePasswordInputChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      alert('Preencha todos os campos');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('A nova senha e a confirmação devem ser iguais');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      alert('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      alert('Senha alterada com sucesso!');
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      alert(error.message || 'Erro ao alterar senha. Tente novamente.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const getMostRecentResponse = () => {
    if (responses.length === 0) return null;
    // As respostas já estão ordenadas por data (mais recente primeiro)
    return responses[0];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  if (showForm) {
    const mostRecentResponse = getMostRecentResponse();
    // Extrair apenas os dados do formulário, excluindo metadados
    const initialData = mostRecentResponse ? {
      ...mostRecentResponse,
      id: undefined,
      oscId: undefined,
      createdAt: undefined
    } : null;
    
    return <FormWizard 
      onComplete={handleFormComplete} 
      onCancel={handleFormCancel} 
      initialData={initialData}
    />;
  }

  if (showThankYou) {
    return <ThankYou onReturnToDashboard={handleReturnToDashboard} />;
  }

  if (selectedResponse) {
    return <ResponseDetails response={selectedResponse} onBack={() => setSelectedResponse(null)} />;
  }

  return (
    <Container>
      <DashboardHeader>
        <WelcomeSection>
          <Title style={{ margin: '0 0 8px 0', lineHeight: '1.2' }}>Bem-vindo(a), {currentUser?.oscData?.nomeResponsavel || 'OSC'}!</Title>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
             <Subtitle style={{ margin: '0', lineHeight: '1.4', flex: 1 }}>
               <div style={{ marginBottom: '4px' }}>
                 <strong>Nome:</strong> {currentUser?.oscData?.nomeInstituicao || currentUser?.oscData?.razaoSocial || 'Nome não encontrado'}
               </div>
               <div>
                 <strong>CNPJ:</strong> {currentUser?.oscData?.cnpj || 'CNPJ não encontrado'}
               </div>
             </Subtitle>
            <div style={{ display: 'flex', gap: '8px' }}>
              <ProfileButton onClick={handleOpenProfile} title="Editar perfil da organização">
                👤
              </ProfileButton>
              <ProfileButton onClick={handleOpenPasswordModal} title="Alterar senha">
                🔒
              </ProfileButton>
            </div>
          </div>
        </WelcomeSection>
        
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Button onClick={() => setShowForm(true)}>
            Nova Resposta
          </Button>
          <LogoutButton variant="secondary" onClick={logout}>
            Sair
          </LogoutButton>
        </div>
      </DashboardHeader>

      {hasPartialResponse && partialResponseData && (
        <PartialResponseCard>
          <div className="partial-info">
            <h3>Resposta em Andamento</h3>
            <div className="date">
              Última modificação: {formatDate(partialResponseData.lastSaved)}
            </div>
          </div>
          <ContinueButton onClick={() => setShowForm(true)}>
            Continuar Respondendo
          </ContinueButton>
        </PartialResponseCard>
      )}

      <Card>
        <h2 style={{ marginBottom: '24px', color: '#212529' }}>Histórico de Respostas</h2>
        
        {loading ? (
          <p>Carregando respostas...</p>
        ) : responses.length === 0 ? (
          <EmptyState>
            <h3>Nenhuma resposta encontrada</h3>
            <p>Você ainda não respondeu nenhum formulário.</p>
            <Button onClick={() => setShowForm(true)}>
              Responder Primeiro Formulário
            </Button>
          </EmptyState>
        ) : (
          <ResponsesList>
            {responses.map((response) => (
              <ResponseCard key={response.id}>
                <div className="response-info">
                  <h3>Resposta #{response.id || 'N/A'}</h3>
                  <div className="date">{formatDate(response.createdAt)}</div>
                </div>
                <ViewButton onClick={() => setSelectedResponse(response)}>
                  Ver Detalhes
                </ViewButton>
              </ResponseCard>
            ))}
          </ResponsesList>
        )}
      </Card>
      
      {showProfileModal && (
        <Modal onClick={(e) => e.target === e.currentTarget && handleCloseProfile()}>
          <ModalContent>
            <ModalHeader>
              <h2>Editar Perfil da Organização</h2>
              <CloseButton onClick={handleCloseProfile}>×</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>CNPJ</Label>
              <Input 
                type="text" 
                value={currentUser?.oscData?.cnpj || ''} 
                disabled 
              />
              <DisabledNote>O CNPJ não pode ser alterado</DisabledNote>
            </FormGroup>
            
            <FormGroup>
              <Label>E-mail</Label>
              <Input 
                type="email" 
                value={currentUser?.email || ''} 
                disabled 
              />
              <DisabledNote>O e-mail não pode ser alterado</DisabledNote>
            </FormGroup>
            
            <FormGroup>
              <Label>Razão Social *</Label>
              <Input 
                type="text" 
                value={profileData.razaoSocial}
                onChange={(e) => handleProfileInputChange('razaoSocial', e.target.value)}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Nome Fantasia</Label>
              <Input 
                type="text" 
                value={profileData.nomeInstituicao}
                onChange={(e) => handleProfileInputChange('nomeInstituicao', e.target.value)}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Endereço *</Label>
              <TextArea 
                value={profileData.endereco}
                onChange={(e) => handleProfileInputChange('endereco', e.target.value)}
                required
              />
            </FormGroup>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <FormGroup>
                <Label>Número</Label>
                <Input 
                  type="text" 
                  value={profileData.numero}
                  onChange={(e) => handleProfileInputChange('numero', e.target.value)}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Complemento</Label>
                <Input 
                  type="text" 
                  value={profileData.complemento}
                  onChange={(e) => handleProfileInputChange('complemento', e.target.value)}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Bairro</Label>
                <Input 
                  type="text" 
                  value={profileData.bairro}
                  onChange={(e) => handleProfileInputChange('bairro', e.target.value)}
                />
              </FormGroup>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
              <FormGroup>
                <Label>Cidade *</Label>
                <Input 
                  type="text" 
                  value={profileData.cidade}
                  onChange={(e) => handleProfileInputChange('cidade', e.target.value)}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>UF *</Label>
                <Input 
                  type="text" 
                  value={profileData.uf}
                  onChange={(e) => handleProfileInputChange('uf', e.target.value)}
                  maxLength={2}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>CEP</Label>
                <Input 
                  type="text" 
                  value={profileData.cep}
                  onChange={(e) => handleProfileInputChange('cep', e.target.value)}
                />
              </FormGroup>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <FormGroup>
                <Label>WhatsApp da Organização</Label>
                <Input 
                  type="tel" 
                  value={profileData.whatsapp}
                  onChange={(e) => handleProfileInputChange('whatsapp', e.target.value)}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>WhatsApp do Responsável</Label>
                <Input 
                  type="tel" 
                  value={profileData.whatsappResponsavel}
                  onChange={(e) => handleProfileInputChange('whatsappResponsavel', e.target.value)}
                />
              </FormGroup>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <FormGroup>
                <Label>Site</Label>
                <Input 
                  type="url" 
                  value={profileData.site}
                  onChange={(e) => handleProfileInputChange('site', e.target.value)}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Instagram</Label>
                <Input 
                  type="text" 
                  value={profileData.instagram}
                  onChange={(e) => handleProfileInputChange('instagram', e.target.value)}
                />
              </FormGroup>
            </div>
            
            <FormGroup>
              <Label>Nome do Responsável *</Label>
              <Input 
                type="text" 
                value={profileData.nomeResponsavel}
                onChange={(e) => handleProfileInputChange('nomeResponsavel', e.target.value)}
                required
              />
            </FormGroup>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <FormGroup>
                <Label>Cargo do Responsável</Label>
                <Input 
                  type="text" 
                  value={profileData.cargo}
                  onChange={(e) => handleProfileInputChange('cargo', e.target.value)}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>E-mail do Responsável</Label>
                <Input 
                  type="email" 
                  value={profileData.emailResponsavel}
                  onChange={(e) => handleProfileInputChange('emailResponsavel', e.target.value)}
                />
              </FormGroup>
            </div>
            
            <ButtonGroup>
              <Button 
                variant="secondary" 
                onClick={handleCloseProfile}
                disabled={profileLoading}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveProfile}
                disabled={profileLoading || !profileData.razaoSocial || !profileData.endereco || !profileData.cidade || !profileData.uf || !profileData.nomeResponsavel}
              >
                {profileLoading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {showPasswordModal && (
        <Modal onClick={handleClosePasswordModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Alterar Senha</h2>
              <CloseButton onClick={handleClosePasswordModal}>
                ×
              </CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Senha Atual *</Label>
              <Input 
                type="password" 
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                placeholder="Digite sua senha atual"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Nova Senha *</Label>
              <Input 
                type="password" 
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                placeholder="Digite a nova senha (mínimo 6 caracteres)"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Confirmar Nova Senha *</Label>
              <Input 
                type="password" 
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                placeholder="Confirme a nova senha"
                required
              />
            </FormGroup>
            
            <ButtonGroup>
              <Button 
                variant="secondary" 
                onClick={handleClosePasswordModal}
                disabled={passwordLoading}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleChangePassword}
                disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
              >
                {passwordLoading ? 'Alterando...' : 'Alterar Senha'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default Dashboard;